import { useEffect, useState } from "react";
import { XMarkIcon, ExclamationTriangleIcon, ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import ScopeIcon from "./ScopeIcon";
import type { MigrationRowData, TargetSummary } from "./utils/constants";

type Mode = "run" | "mark";

interface Props {
  open: boolean;
  rows: MigrationRowData[];
  targets: TargetSummary[];
  onCancel: () => void;
  onConfirm: (options: { mode: Mode; backup: boolean; reason: string }) => void;
}

function hasDestructiveSql(rows: MigrationRowData[]): boolean {
  return rows.some((r) => /DROP|TRUNCATE/i.test(`${r.folderName} ${r.fileName}`));
}

export default function BulkApplyModal({ open, rows, targets, onCancel, onConfirm }: Props) {
  const [backup, setBackup] = useState(true);
  const [migrationsCollapsed, setMigrationsCollapsed] = useState(false);
  const [mode, setMode] = useState<Mode>("run");
  const [reason, setReason] = useState("");

  // Reset mode + reason whenever the modal opens
  useEffect(() => {
    if (open) {
      setMode("run");
      setReason("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const totalOps = rows.length * targets.length;
  const isDestructive = hasDestructiveSql(rows);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
      <div role="dialog" aria-modal="true" className="relative w-full max-w-xl bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-base font-bold text-slate-900">
              {mode === "mark"
                ? `Mark ${rows.length} migration${rows.length !== 1 ? "s" : ""} as applied on ${targets.length} target${targets.length !== 1 ? "s" : ""}`
                : `Apply ${rows.length} migration${rows.length !== 1 ? "s" : ""} to ${targets.length} target${targets.length !== 1 ? "s" : ""}`}
            </h2>
            <p className="mt-0.5 text-[11px] text-slate-500">
              {totalOps} total operation{totalOps !== 1 ? "s" : ""} —{" "}
              {mode === "mark"
                ? "records migrations as applied without executing SQL."
                : "this will execute each migration against every selected target."}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">

          {/* Mode picker */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">What do you want to do?</div>
            <div className="space-y-1.5">
              <label className={`flex items-start gap-2.5 p-3 border rounded-lg cursor-pointer transition-colors ${mode === "run" ? "border-blue-300 bg-blue-50/60" : "border-slate-200 hover:bg-slate-50"}`}>
                <input
                  type="radio"
                  checked={mode === "run"}
                  onChange={() => setMode("run")}
                  className="mt-0.5 accent-blue-600"
                />
                <div>
                  <div className="text-sm font-semibold text-slate-900">Run the SQL</div>
                  <div className="text-xs text-slate-500">Execute the migration on the selected targets.</div>
                </div>
              </label>
              <label className={`flex items-start gap-2.5 p-3 border rounded-lg cursor-pointer transition-colors ${mode === "mark" ? "border-amber-300 bg-amber-50/60" : "border-slate-200 hover:bg-slate-50"}`}>
                <input
                  type="radio"
                  checked={mode === "mark"}
                  onChange={() => setMode("mark")}
                  className="mt-0.5 accent-amber-600"
                />
                <div>
                  <div className="text-sm font-semibold text-slate-900">Just mark as already applied</div>
                  <div className="text-xs text-slate-500">Record as successful without running. Use when you applied them manually via mysql CLI.</div>
                </div>
              </label>
            </div>
          </div>

          {/* Reason input (mark mode only) */}
          {mode === "mark" && (
            <div>
              <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
                Reason (optional)
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Applied via mysql CLI on 2026-06-07"
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
              />
            </div>
          )}

          {/* Migrations list — collapsible */}
          <div className="bg-slate-50 rounded-lg border border-slate-100 overflow-hidden">
            <button
              type="button"
              onClick={() => setMigrationsCollapsed((v) => !v)}
              className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-slate-100 text-left"
            >
              {migrationsCollapsed
                ? <ChevronRightIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                : <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
              <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                Migrations ({rows.length})
              </span>
            </button>
            {!migrationsCollapsed && (
              <div className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <div key={row.id} className="flex items-center gap-3 px-4 py-2 bg-white">
                    <ScopeIcon scope={row.scope} inferred={row.scopeInferred} />
                    <div className="min-w-0 flex-1">
                      <span className="text-[12px] font-mono text-slate-700 truncate block">
                        <span className="text-slate-500">{row.folderName}/</span>{row.fileName}
                      </span>
                    </div>
                    <span className="text-[10px] text-blue-600 font-bold shrink-0">v{row.version}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Targets list */}
          <div className="bg-slate-50 rounded-lg border border-slate-100 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5">
              <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                Targets ({targets.length})
              </span>
            </div>
            <div className="divide-y divide-slate-100">
              {targets.map((t) => (
                <div key={t.id} className="flex items-center gap-3 px-4 py-2 bg-white">
                  <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-md border ${
                    t.kind === "tenant"
                      ? "bg-sky-100 text-sky-700 border-sky-200"
                      : "bg-purple-100 text-purple-700 border-purple-200"
                  }`}>
                    {t.kind}
                  </span>
                  <span className="text-[12px] text-slate-700 flex-1">{t.name}</span>
                  <span className="text-[10px] text-slate-400 font-mono">{t.databaseName}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Destructive warning — only in run mode */}
          {mode === "run" && isDestructive && (
            <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
              <ExclamationTriangleIcon className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              <p className="text-[12px] text-red-700">
                One or more migration names contain <strong>DROP</strong> or <strong>TRUNCATE</strong>.
                Verify these are intentional — destructive SQL cannot be automatically reversed.
              </p>
            </div>
          )}

          {/* Backup checkbox — only in run mode */}
          {mode === "run" && (
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={backup}
                onChange={(e) => setBackup(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 bg-white text-blue-600 focus:ring-blue-500/30"
              />
              <div>
                <span className="text-[13px] text-slate-700 font-medium group-hover:text-slate-900">
                  Take an automatic backup before each tenant (recommended)
                </span>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  A snapshot is stored for 7 days and can be used to roll back if needed.
                </p>
              </div>
            </label>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onCancel}
            className="px-3.5 py-2 text-sm font-medium bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm({ mode, backup, reason: reason.trim() || "Marked applied via bulk" })}
            className={`px-4 py-2 rounded-lg text-sm font-bold text-white ${
              mode === "mark"
                ? "bg-amber-600 hover:bg-amber-700"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {mode === "mark"
              ? `Mark ${totalOps} as applied`
              : `Apply ${totalOps} operation${totalOps !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
